import type { Notification } from "@gjwtrack/domain";
import { CheckCheck } from "lucide-react";
import { useEffect, useRef, type ReactElement } from "react";
import { useNavigate } from "react-router";
import { client, useClient } from "../lib/client.ts";
import { absTime, relTimeAgo, userName } from "../lib/format.ts";
import { Directions, Dropdown, Popup } from "../ui/ring.ts";

const VERB: Record<Notification["kind"], string> = { created: "created", updated: "updated", commented: "commented on", mentioned: "mentioned you in", resolved: "resolved" };

/** The bell panel (Ring Dropdown + Popup opening to the right of the nav): unread first, click to open the issue and mark read. */
export function NotificationsPanel({ anchor, onToggle }: { anchor: ReactElement; onToggle?: (open: boolean) => void }) {
  const state = useClient((s) => s.notifications);
  const schema = useClient((s) => s.schema);
  const display = useClient((s) => s.display);
  const keyIndex = useClient((s) => s.keyIndex);
  const navigate = useNavigate();
  const dropdown = useRef<Dropdown>(null);
  const keyOf = (issueId: number | null): string | null => {
    if (issueId === null || !schema) return null;
    const i = display[issueId];
    if (i) return `${schema.projectsById.get(i.projectId)?.key ?? i.projectId}-${i.number}`;
    return Object.entries(keyIndex).find(([, id]) => id === issueId)?.[0] ?? null;
  };
  useEffect(() => {
    // Resolve issue keys for notifications we cannot name yet.
    const missing = state.items.filter((n) => n.issueId !== null && !display[n.issueId!]).map((n) => n.issueId!);
    for (const id of new Set(missing)) void client.ensureIssue(id);
  }, [state.items, display]);
  const openIssue = (n: Notification): void => {
    if (n.readAt === null) void client.markRead([n.id]);
    const key = keyOf(n.issueId);
    dropdown.current?.toggle(false);
    if (key) navigate(`/issue/${key}`);
  };
  return (
    <Dropdown
      ref={dropdown}
      anchor={anchor}
      onShow={() => {
        onToggle?.(true);
        if (!state.loaded) void client.loadNotifications();
      }}
      onHide={() => onToggle?.(false)}
    >
      <Popup directions={[Directions.RIGHT_BOTTOM, Directions.RIGHT_TOP, Directions.TOP_RIGHT]} offset={8} className="w-[26rem]">
        <div className="flex items-center gap-2 border-b border-line px-2 py-1.5">
          <b>Notifications</b>
          {state.unread > 0 && <span className="chip chip-1">{state.unread} unread</span>}
          <div className="flex-1" />
          <button className="flex items-center gap-1 text-xs text-muted hover:text-ink" onClick={() => void client.markRead("all")} disabled={state.unread === 0}>
            <CheckCheck size={14} /> Mark all read
          </button>
        </div>
        <ul className="max-h-[70vh] overflow-y-auto">
          {state.loaded && state.items.length === 0 && <li className="px-3 py-4 text-muted">Nothing yet. You are told when issues you watch, report or are assigned change, and when someone mentions you.</li>}
          {state.items.map((n) => (
            <li key={n.id}>
              <button className={`flex w-full gap-2 border-b border-line px-3 py-2 text-left hover:bg-sunken ${n.readAt === null ? "" : "opacity-60"}`} onClick={() => openIssue(n)}>
                <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${n.readAt === null ? "bg-accent" : "bg-transparent"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px]">
                    <b>{n.actorId === null ? "Someone" : userName(schema, n.actorId)}</b> {VERB[n.kind]} <span className="font-mono text-xs text-value">{keyOf(n.issueId) ?? "…"}</span>
                  </span>
                  <span className="block truncate text-[13px]">{n.summary}</span>
                  {Array.isArray(n.detail.changed) && <span className="block text-xs text-muted">Changed: {(n.detail.changed as string[]).join(", ")}</span>}
                  {typeof n.detail.text === "string" && <span className="block truncate text-xs text-muted">“{n.detail.text}”</span>}
                  <span className="block text-xs text-faint" title={absTime(n.createdAt)}>{relTimeAgo(n.createdAt)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Popup>
    </Dropdown>
  );
}
